import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@src/lib/utils/cn";
import { IconProps } from "@src/lib/icons/types";

// Status fill + text pairs shared by Banner and AnimatedBanner.
export const bannerVariants = cva("", {
  variants: {
    variant: {
      info: "bg-status-info-bg text-status-info-text",
      success: "bg-status-success-bg text-status-success-text",
      warning: "bg-status-warning-bg text-status-warning-text",
      error: "bg-status-error-bg text-status-error-text",
    },
  },
  defaultVariants: {
    variant: "error",
  },
});

export type BannerVariant = NonNullable<
  VariantProps<typeof bannerVariants>["variant"]
>;

type Props = {
  Icon?: React.ComponentType<IconProps>;
  children: React.ReactNode;
  className?: string;
  /** Status colour. Defaults to "error", the original Banner look. */
  variant?: BannerVariant;
  /** Trailing slot, e.g. a ghost Button. */
  action?: React.ReactNode;
};

const Banner: React.FC<Props> = ({
  Icon,
  className,
  children,
  variant = "error",
  action,
}) => {
  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      data-variant={variant}
      className={cn(
        "flex items-start gap-2 self-stretch rounded-lg p-3",
        bannerVariants({ variant }),
        className,
      )}
    >
      {Icon ? <Icon className="size-4 shrink-0 translate-y-0.5" /> : null}
      <div className="min-w-0 flex-1 text-xs-v4 break-words">{children}</div>
      {action ? (
        <div className="-my-1 flex shrink-0 items-center">{action}</div>
      ) : null}
    </div>
  );
};

export default Banner;
